import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
// Hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
// Firebase
import { projectFirestore } from "../../firebase/config";
// Components
import Delete from "../../components/Delete";
import Error from "../../components/Error";
import Loading from "../../components/Loading";

// Styles
import "../../styles/pages/Projects.css";

function Projects() {
  // Router utilities
  const location = useLocation();
  const navigate = useNavigate();
  // Read contributor filter from URL
  const query = new URLSearchParams(location.search);
  const queryUserId = query.get("user");
  // Signed in user
  const { user } = useAuthContext();
  // Sort & filter state
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(queryUserId || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllTags, setShowAllTags] = useState(false);
  // Maximum number of technologies shown before "show more"
  const TAG_LIMIT = 10;
  // Firestore hooks
  const { documents: projects, error } = useCollection("projects", null, {
    field: sortField,
    direction: sortDir,
  });
  const { deleteDocument, response } = useFirestore("projects");
  // UI state
  const [showModal, setShowModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  // Map of user ID -> user profile
  const [owners, setOwners] = useState({});
  // Non-blocking contributor profile error
  const [errorMessage, setErrorMessage] = useState(null);

  // Keep selected contributor synced with URL
  useEffect(() => {
    setSelectedOwner(queryUserId || "");
  }, [queryUserId]);

  // Open delete modal
  const openDeleteModal = (id) => {
    setTargetId(id);
    setShowModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!targetId) return;

    await deleteDocument(targetId);

    setShowModal(false);
    setTargetId(null);
  };

  // Fetch project owners and tagged users
  useEffect(() => {
    const fetchUserProfiles = async () => {
      if (!projects) return;

      // Reset previous contributor loading error
      setErrorMessage(null);
      // Collect all unique user IDs used by projects
      const allUserIds = new Set();

      projects.forEach((project) => {
        // Add project creator
        if (project.createdBy) allUserIds.add(project.createdBy);

        // Add tagged contributors
        if (Array.isArray(project.taggedUsers))
          project.taggedUsers.forEach((uid) => {
            allUserIds.add(uid);
          });
      });

      // Build user lookup map
      const userMap = {};
      await Promise.all(
        [...allUserIds].map(async (uid) => {
          try {
            const userDoc = await getDoc(doc(projectFirestore, "users", uid));

            if (userDoc.exists())
              userMap[uid] = {
                id: userDoc.id,
                ...userDoc.data(),
              };
          } catch (err) {
            setErrorMessage("Some contributor information could not be loaded.");
          }
        })
      );

      setOwners(userMap);
    };

    fetchUserProfiles();
  }, [projects]);

  // Unique technologies/tags across all projects
  const allTags = useMemo(() => {
    if (!projects) return [];

    return [...new Set(projects.flatMap((project) => project.tags || []))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [projects]);

  // Build unique contributor list for contributor filter
  const contributorOptions = useMemo(() => {
    return Object.entries(owners)
      .map(([uid, info]) => ({
        uid,
        ...info,
      }))
      .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
  }, [owners]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    const normalizedSearch = searchTerm.trim().toLowerCase();

    return projects.filter((project) => {
      // All selected tags must exist on project
      const matchesTags =
        selectedTags.length === 0 || selectedTags.every((tag) => project.tags?.includes(tag));

      // Contributor can be creator or tagged user
      const matchesOwner =
        !selectedOwner ||
        project.createdBy === selectedOwner ||
        project.taggedUsers?.includes(selectedOwner);

      // Search project name, summary and technologies
      const matchesSearch =
        !normalizedSearch ||
        project.name?.toLowerCase().includes(normalizedSearch) ||
        project.summary?.toLowerCase().includes(normalizedSearch) ||
        project.tags?.some((tag) => tag.toLowerCase().includes(normalizedSearch));

      return matchesTags && matchesOwner && matchesSearch;
    });
  }, [projects, selectedTags, selectedOwner, searchTerm]);

  // Limit visible tags unless user expands list
  const visibleTags = showAllTags ? allTags : allTags.slice(0, TAG_LIMIT);

  // Check whether any filters are active
  const hasActiveFilters = selectedTags.length > 0 || selectedOwner || searchTerm.trim();

  // Toggle technology filter
  const toggleTag = (tag) => {
    setSelectedTags((current) =>
      current.includes(tag)
        ? current.filter((selectedTag) => selectedTag !== tag)
        : [...current, tag]
    );
  };

  // Reset all project filters
  const resetFilters = () => {
    setSelectedTags([]);
    setSelectedOwner("");
    setSearchTerm("");
    setShowAllTags(false);

    navigate("/projects");
  };

  // Update contributor filter and URL
  const handleOwnerChange = (e) => {
    const uid = e.target.value;

    setSelectedOwner(uid);
    navigate(uid ? `/projects?user=${encodeURIComponent(uid)}` : "/projects");
  };

  // Show site-wide loading state while projects are loading
  if (!projects && !error) return <Loading message="Loading projects" />;
  // Show site-wide error if projects collection failed to load
  if (error) return <Error message={error} />;

  return (
    <main className="projects-page text-light">
      {/* HERO */}
      <section className="projects-hero position-relative overflow-hidden">
        {/* Background Grid */}
        <div className="projects-grid-background position-absolute top-0 start-0 w-100 h-100" />
        <div className="container position-relative">
          <div className="row align-items-end g-5">
            {/* Hero Content */}
            <div className="col-lg-8">
              <motion.div
                className="projects-hero-content"
                initial={{
                  opacity: 0,
                  y: 30,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.7,
                }}
              >
                <span className="section-label d-block mb-4">01 / OUR WORK</span>
                <h1 className="projects-title fw-bold mb-4">
                  Things we&apos;ve
                  <br />
                  <span className="text-danger">built.</span>
                </h1>
                <p className="projects-hero-copy text-secondary mb-0">
                  Experiments, client work and ideas we&apos;ve turned into real products across
                  software, data, engineering and intelligent systems.
                </p>
              </motion.div>
            </div>
            {/* Hero Statistics */}
            <div className="col-lg-4">
              <motion.div
                className="projects-hero-meta border-top border-secondary-subtle"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                transition={{
                  delay: 0.4,
                  duration: 0.6,
                }}
              >
                <div className="d-flex justify-content-between align-items-center py-3 border-bottom border-secondary-subtle">
                  <span className="text-secondary small">Projects</span>
                  <strong>{projects?.length || 0}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center py-3 border-bottom border-secondary-subtle">
                  <span className="text-secondary small">Showing</span>
                  <strong>{filteredProjects.length}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center py-3">
                  <span className="text-secondary small">Technologies</span>
                  <strong>{allTags.length}</strong>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* FILTERS */}
      <section className="projects-controls-section border-top border-bottom border-secondary-subtle">
        <div className="container">
          {/* Search / Sort / Contributor */}
          <div className="row g-3 align-items-end">
            {/* Search */}
            <div className="col-lg-6">
              <label
                htmlFor="project-search"
                className="projects-control-label d-block mb-2 font-monospace text-secondary"
              >
                SEARCH
              </label>
              <div className="projects-search position-relative">
                <i className="bi bi-search projects-search-icon position-absolute top-50 translate-middle-y" />
                <input
                  id="project-search"
                  type="search"
                  className="form-control projects-control-input"
                  placeholder="Search projects, descriptions or technologies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {/* Clear Search */}
                {searchTerm && (
                  <button
                    type="button"
                    className="projects-search-clear btn border-0 position-absolute top-50 translate-middle-y"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>
            {/* Sort Field */}
            <div className="col-sm-6 col-lg-2">
              <label
                htmlFor="project-sort"
                className="projects-control-label d-block mb-2 font-monospace text-secondary"
              >
                SORT BY
              </label>
              <select
                id="project-sort"
                className="form-select projects-control-input"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="createdAt">Created</option>
                <option value="lastModified">Last modified</option>
              </select>
            </div>
            {/* Sort Direction */}
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-outline-secondary projects-sort-direction"
                onClick={() => setSortDir((current) => (current === "asc" ? "desc" : "asc"))}
                aria-label={`Sort ${sortDir === "asc" ? "descending" : "ascending"}`}
                title="Toggle sort direction"
              >
                <i className={`bi ${sortDir === "asc" ? "bi-sort-up" : "bi-sort-down"}`} />
              </button>
            </div>
            {/* Contributor */}
            <div className="col-sm-6 col-lg">
              <label
                htmlFor="project-owner"
                className="projects-control-label d-block mb-2 font-monospace text-secondary"
              >
                CONTRIBUTOR
              </label>
              <select
                id="project-owner"
                className="form-select projects-control-input"
                value={selectedOwner}
                onChange={handleOwnerChange}
              >
                <option value="">Everyone</option>
                {contributorOptions.map((contributor) => (
                  <option key={contributor.uid} value={contributor.uid}>
                    {contributor.name || contributor.email || "Contributor"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Technology Filters */}
          <div className="projects-tag-filter mt-4 pt-4 border-top border-secondary-subtle">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
              <span className="projects-control-label font-monospace text-secondary">
                FILTER BY TECHNOLOGY
              </span>
              {/* Reset Filters */}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="btn btn-link btn-sm text-danger text-decoration-none p-0 d-inline-flex align-items-center gap-2"
                >
                  Reset filters
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {/* Empty Technology State */}
              {allTags.length === 0 && (
                <span className="text-secondary small">No technologies available yet.</span>
              )}
              {/* Technology Buttons */}
              {visibleTags.map((tag) => {
                const selected = selectedTags.includes(tag);

                return (
                  <button
                    key={tag}
                    type="button"
                    className={`projects-tag-button ${
                      selected ? "projects-tag-button-active" : ""
                    }`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                    {selected && <i className="bi bi-x ms-1" />}
                  </button>
                );
              })}
              {/* Show More / Less */}
              {allTags.length > TAG_LIMIT && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-secondary text-decoration-none"
                  onClick={() => setShowAllTags((current) => !current)}
                >
                  {showAllTags ? "Show less" : `+${allTags.length - TAG_LIMIT} more`}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* NON-BLOCKING ERRORS */}
      {(errorMessage || response.error) && (
        <section className="pt-4">
          <div className="container">
            {/* Contributor Profile Error */}
            {errorMessage && (
              <div className="alert projects-alert mb-3" role="alert">
                <i className="bi bi-exclamation-circle me-2 text-danger" />
                {errorMessage}
              </div>
            )}
            {/* Delete Error */}
            {response.error && (
              <div className="alert projects-alert mb-0" role="alert">
                <i className="bi bi-exclamation-circle me-2 text-danger" />
                {response.error}
              </div>
            )}
          </div>
        </section>
      )}
      {/* PROJECT LIST */}
      <section className="projects-list-section">
        <div className="container">
          {/* Project List Header */}
          <div className="projects-list-header d-flex align-items-center justify-content-between gap-3 pb-3 mb-4 border-bottom border-secondary-subtle">
            <span className="font-monospace text-secondary">
              {hasActiveFilters ? "FILTERED PROJECTS" : "ALL PROJECTS"}
            </span>
            <span className="font-monospace text-secondary">
              {filteredProjects.length}
              {filteredProjects.length === 1 ? " PROJECT" : " PROJECTS"}
            </span>
          </div>
          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <motion.div
              className="projects-empty-state text-center py-5"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
            >
              <span className="empty-state-code d-block font-monospace text-danger mb-2">404</span>
              <h2 className="fw-bold mb-3">Nothing matched that.</h2>
              <p className="text-secondary mb-4">
                Try a different search, contributor or technology.
              </p>
              <button type="button" onClick={resetFilters} className="btn btn-outline-light">
                Clear filters
              </button>
            </motion.div>
          )}
          {/* Projects Grid */}
          <div className="row g-4">
            <AnimatePresence>
              {filteredProjects.map((project, index) => {
                // Build contributor UID list
                const contributorIds = [project.createdBy, ...(project.taggedUsers || [])].filter(
                  Boolean
                );
                // Remove duplicate contributor IDs
                const uniqueContributorIds = [...new Set(contributorIds)];
                // Convert contributor IDs to profile objects
                const contributors = uniqueContributorIds
                  .map((uid) => {
                    const contributor = owners[uid];
                    if (!contributor) return null;

                    return {
                      uid,
                      ...contributor,
                    };
                  })
                  .filter(Boolean);

                // Check edit/delete permissions
                const canEdit =
                  user?.uid === project.createdBy || project.taggedUsers?.includes(user?.uid);
                const canDelete = user?.uid === project.createdBy;
                // Display project number
                const projectNumber = String(index + 1).padStart(2, "0");

                return (
                  <motion.div
                    layout
                    key={project.id}
                    className="col-md-6 col-xl-4"
                    initial={{
                      opacity: 0,
                      y: 30,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.96,
                    }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.05,
                    }}
                  >
                    <article className="project-display-card h-100 d-flex flex-column overflow-hidden">
                      {/* Project Visual */}
                      <Link
                        to={`/projects/${project.id}`}
                        className="project-display-visual position-relative d-block overflow-hidden text-decoration-none"
                        aria-label={`View ${project.name}`}
                      >
                        {/* Background Grid */}
                        <div className="project-card-grid position-absolute top-0 start-0 w-100 h-100" />
                        {/* Project Watermark */}
                        <span className="project-card-watermark position-absolute">
                          {projectNumber}
                        </span>
                        {/* Visual Header */}
                        <div className="project-visual-header position-relative d-flex justify-content-between align-items-center">
                          <span>PROJECT_{projectNumber}</span>
                          <span>BENOMA</span>
                        </div>
                        {/* Visual Centre */}
                        <div className="project-visual-center position-relative d-flex align-items-center justify-content-center gap-2">
                          <span className="visual-bracket">{"<"}</span>
                          <strong>{project.tags?.[0] || "project"}</strong>
                          <span className="visual-bracket">{" />"}</span>
                        </div>
                        {/* Visual Footer */}
                        <div className="project-visual-footer position-relative d-flex justify-content-between align-items-center">
                          <span>SELECTED WORK</span>
                          <span>↗</span>
                        </div>
                      </Link>
                      {/* Project Content */}
                      <div className="project-display-content d-flex flex-column flex-grow-1 p-4">
                        {/* Project Heading */}
                        <div className="project-card-heading d-flex justify-content-between gap-3 mb-3">
                          <div>
                            <span className="project-type d-block mb-2">
                              {project.tags?.length
                                ? project.tags.slice(0, 2).join(" • ")
                                : "PROJECT"}
                            </span>
                            <Link
                              to={`/projects/${project.id}`}
                              className="text-light text-decoration-none"
                            >
                              <h2 className="h3 fw-bold mb-0">{project.name}</h2>
                            </Link>
                          </div>
                          <span className="project-index font-monospace text-secondary">
                            {projectNumber}
                          </span>
                        </div>
                        {/* Project Summary */}
                        <p className="project-summary text-secondary mb-4">
                          {project.summary ||
                            "Explore this project and learn more about how it was built."}
                        </p>
                        {/* Bottom section keeps tags, contributors and actions together */}
                        <div className="project-card-bottom mt-auto">
                          {/* Project Tags */}
                          {project.tags?.length > 0 && (
                            <div className="project-card-tags d-flex flex-wrap gap-2 mb-3">
                              {project.tags.map((tag) => (
                                <button
                                  type="button"
                                  key={tag}
                                  className="project-card-tag"
                                  onClick={() => toggleTag(tag)}
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          )}
                          {/* Contributors */}
                          {contributors.length > 0 && (
                            <div className="project-contributors d-flex align-items-center justify-content-between gap-3 pt-3 border-top border-secondary-subtle">
                              <div className="contributor-avatars d-flex align-items-center">
                                {contributors.slice(0, 4).map((contributor) => {
                                  const contributorName =
                                    contributor.name || contributor.email || "Contributor";

                                  return (
                                    <Link
                                      key={contributor.uid}
                                      to={`/profile/${encodeURIComponent(contributorName)}`}
                                      title={contributorName}
                                      className="contributor-avatar rounded-circle d-flex align-items-center justify-content-center text-decoration-none"
                                    >
                                      {/* Contributor Image */}
                                      {contributor.photoURL ? (
                                        <img
                                          src={contributor.photoURL}
                                          alt={contributorName}
                                          className="w-100 h-100 rounded-circle"
                                        />
                                      ) : (
                                        <span>{contributorName.charAt(0).toUpperCase()}</span>
                                      )}
                                    </Link>
                                  );
                                })}
                                {/* Additional Contributor Count */}
                                {contributors.length > 4 && (
                                  <span className="contributor-more rounded-circle d-flex align-items-center justify-content-center">
                                    +{contributors.length - 4}
                                  </span>
                                )}
                              </div>
                              <span className="contributors-label text-secondary small">
                                {contributors.length === 1
                                  ? "1 contributor"
                                  : `${contributors.length} contributors`}
                              </span>
                            </div>
                          )}
                          {/* Project Actions */}
                          <div className="project-card-actions d-flex align-items-center justify-content-between gap-3 mt-4">
                            {/* View Project */}
                            <Link
                              to={`/projects/${project.id}`}
                              className="view-project-link d-inline-flex align-items-center gap-2 text-decoration-none"
                            >
                              View project
                              <span>→</span>
                            </Link>
                            {/* Management Actions */}
                            {(canEdit || canDelete) && (
                              <div className="project-management-actions d-flex align-items-center gap-2">
                                {/* Edit Project */}
                                {canEdit && (
                                  <Link
                                    to={`/projects/${project.id}/edit`}
                                    className="btn btn-outline-light btn-sm project-icon-button d-inline-flex align-items-center justify-content-center"
                                    title="Edit project"
                                    aria-label={`Edit ${project.name}`}
                                  >
                                    <i className="bi bi-pencil" />
                                  </Link>
                                )}
                                {/* Delete Project */}
                                {canDelete && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm project-icon-button d-inline-flex align-items-center justify-content-center"
                                    onClick={() => openDeleteModal(project.id)}
                                    title="Delete project"
                                    aria-label={`Delete ${project.name}`}
                                  >
                                    <i className="bi bi-trash" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="projects-cta-section bg-danger text-white">
        <div className="container">
          <motion.div
            className="projects-cta row align-items-end g-4"
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.7,
            }}
          >
            <div className="col-lg-9">
              <span className="projects-cta-label font-monospace fw-bold d-block mb-3">
                HAVE SOMETHING YOU WANT BUILT?
              </span>
              <h2 className="projects-cta-title fw-bold mb-0">
                Your idea could be
                <br />
                next.
              </h2>
            </div>
            <div className="col-lg-3 text-lg-end">
              <Link
                to="/contact"
                className="btn btn-light btn-lg d-inline-flex align-items-center gap-2"
              >
                Start a Project
                <span>→</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* DELETE MODAL */}
      {showModal && (
        <Delete
          busy={response.isPending}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowModal(false);
            setTargetId(null);
          }}
        />
      )}
    </main>
  );
}

export default Projects;
